/**
 * Rubato: the search drawer.
 *
 * Round 62 part 2. Horizon's search modal is not used. This opens the site's own flyout and
 * fills it from Shopify's predictive search JSON, so the rows are the theme's markup rather
 * than a component's: Mars 12 rows, no icons, the collection card's proportions on the
 * products, and nothing that was not asked for.
 *
 * The JSON endpoint rather than the section-rendering one, because what comes back is data and
 * the markup stays here. Four products is the ceiling the brief sets; the count in "View all"
 * is Shopify's total, which is larger than four whenever there is more to see.
 */

const ENDPOINT = '/search/suggest.json';
/* sections/rubato-search-cards.liquid: a section of our own that renders four cards and
   nothing else. The results section could not be used. Asked for by file name it renders with
   schema defaults, so the cards come back as shells with no gallery and no price, because what
   fills them lives in the template's JSON; asked for by its key in that JSON, main, it answers
   404. */
const SECTION = '/search?type=product&section_id=rubato-search-cards';
const LIMIT = 4;
const DEBOUNCE = 220;

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/**
 * A suggestion reads as what was typed plus what is being offered. Shopify returns the whole
 * string, so the typed part is found in it rather than assumed to be at the front: searching
 * "denim" can come back "organic cotton denim", where the match is in the middle.
 */
function splitSuggestion(text, query) {
  const haystack = String(text ?? '');
  const needle = String(query ?? '').trim();
  if (!needle) return [haystack, ''];
  const at = haystack.toLowerCase().indexOf(needle.toLowerCase());
  if (at < 0) return ['', haystack];
  return [haystack.slice(0, at + needle.length), haystack.slice(at + needle.length)];
}

function setUpSearch() {
  const dialog = document.querySelector('[data-rubato-search]');
  if (!(dialog instanceof HTMLDialogElement) || dialog.dataset.rubatoWired === 'true') return;
  dialog.dataset.rubatoWired = 'true';

  const input = dialog.querySelector('[data-rubato-search-drawer-input]');
  const empty = dialog.querySelector('[data-rubato-search-empty]');
  const results = dialog.querySelector('[data-rubato-search-results]');
  const page = dialog.querySelector('[data-rubato-search-page]');
  const panel = dialog.querySelector('.rubato-search-drawer__panel');

  /* One breakpoint, named once. From 1024 the cards go into the page cell on the left and the
     panel keeps the field and the suggestions; below it everything is in the sheet. */
  const WIDE = '(min-width: 1024px)';
  const isWide = () => window.matchMedia(WIDE).matches;

  /* Round 62 part 4. The page behind the sheet is held still.
   *
   * A modal dialog stops the page scrolling in Chrome, and that was not enough on a phone:
   * opening the keyboard moved the viewport and took the sheet with it, so the field slid up
   * off the top. The body is pinned at the offset it was already at, which keeps the page
   * exactly where the customer left it, and released to the same offset on close.
   *
   * focus with preventScroll for the same reason: focusing a field near the top of a fixed
   * panel still asks the browser to scroll it into view, and the browser obliges by moving the
   * visual viewport. */
  let lockedAt = 0;

  const lockPage = () => {
    lockedAt = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedAt}px`;
    document.body.style.insetInline = '0';
    document.body.style.overflow = 'hidden';
  };

  const unlockPage = () => {
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('inset-inline');
    document.body.style.removeProperty('overflow');
    window.scrollTo(0, lockedAt);
    /* Round 62 part 6. The header is told the page moved.
     *
     * Pinning the body and letting it go is not a scroll as far as the browser is concerned,
     * so scrolling to the top, opening the drawer and closing it again left the header holding
     * whatever state it had when the page was pinned. A scroll event on the container the
     * header listens to makes it read the position again and settle on it. */
    requestAnimationFrame(() => {
      const wrapper = document.querySelector('.page-wrapper');
      const target = wrapper && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : window;
      target.dispatchEvent(new Event('scroll'));
    });
  };

  const open = () => {
    if (dialog.open) return;
    lockPage();
    /* The page cell claims its column before the dialog paints. It is hidden in the markup so
       a narrow screen never reserves space for it, and on a wide one that attribute has to come
       off at open rather than at the first keystroke, or the panel starts in the left column
       and jumps right when results arrive. */
    if (page) page.hidden = !isWide();
    dialog.showModal();
    /* Autofocused, and the caret is the only focus mark a text field gets: the box ring is off
       for text inputs site-wide since round 62 part 2, which is why the field can take focus
       the moment the panel opens without a rectangle appearing around it. */
    requestAnimationFrame(() => input?.focus({ preventScroll: true }));
  };

  /* Below the wide layout the sheet leaves the way it came, out to the right edge, and the
     dialog closes and the page is released once that has run, so nothing moves under the sheet
     while it goes. A timer stands in for animationend in case the animation never runs. From
     the wide layout up, and with reduced motion, it closes at once as before. Round 63. */
  let closing = false;
  const SLIDE_MS = 260;

  const close = () => {
    if (!dialog.open || closing) return;
    const animate = !isWide() && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate) {
      dialog.close();
      unlockPage();
      return;
    }
    closing = true;
    let fallback = 0;
    const done = () => {
      dialog.removeEventListener('animationend', onEnd);
      clearTimeout(fallback);
      dialog.removeAttribute('data-closing');
      closing = false;
      dialog.close();
      unlockPage();
    };
    const onEnd = (event) => {
      if (event.target === dialog && event.animationName === 'rubato-search-drawer-out') done();
    };
    dialog.addEventListener('animationend', onEnd);
    fallback = setTimeout(done, SLIDE_MS + 150);
    dialog.setAttribute('data-closing', '');
  };

  /* Every way in. The header renders the control more than once, once for the desktop row and
     once for the drawer, so this is delegated rather than bound per element. */
  document.addEventListener('click', (event) => {
    const trigger = typeof event.target?.closest === 'function'
      ? event.target.closest('[data-rubato-search-open], a[href="/search"], .header-actions__search, .search-action')
      : null;
    if (!trigger) return;
    /* A real search results page is still reachable: only the header's control opens the
       drawer, and only when it is not asking for a new tab. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    open();
  });

  dialog.querySelector('[data-rubato-search-close]')?.addEventListener('click', close);
  /* Outside click, the way the menu and the cart drawers do it: the pointer's position is
     measured against the panel rather than the event's target being compared with the dialog.
     Round 62 part 7, item 3. The target test only ever fired below 1024, where the sheet sits
     in an otherwise bare dialog. From 1024 the page cell fills the rest of the modal and takes
     the click itself, so nothing outside the panel was ever the dialog, and with results
     rendered the cards filled that cell as well. Horizon's own isClickedOutside is geometric
     for the same reason, in assets/utilities.js.

     A click carried by the keyboard reports the point 0,0, which reads as outside. Enter on
     the close button or on a row would have closed the drawer from here rather than through
     its own handler, so a synthesised click is left alone: detail is 0 on those and 1 on a
     real one. */
  dialog.addEventListener('click', (event) => {
    if (!dialog.open || event.detail === 0) return;
    const box = panel?.getBoundingClientRect();
    if (!box) {
      if (event.target === dialog) close();
      return;
    }
    const inside =
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom;
    if (!inside) close();
  });
  /* Escape, twice over. A dialog closes itself on Escape and fires cancel, but the field is an
     input[type=search] and the browser gives that key to the field first, to clear it, so the
     dialog never sees it while the caret is in the box. The keydown below is the one that
     actually closes the drawer; the cancel handler stays for the case where focus has moved
     out of the field. */
  /* The browser's own close on Escape would cut the slide off, so it is taken over here. */
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });
  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    close();
  });

  let timer = null;
  let sequence = 0;

  const showEmptyState = () => {
    if (empty) empty.hidden = false;
    if (results) {
      results.hidden = true;
      results.replaceChildren();
    }
    if (page) {
      page.hidden = !isWide();
      page.replaceChildren();
    }
  };

  const render = (data, query, cards) => {
    if (!results) return;
    const suggestions = data?.resources?.results?.queries ?? [];
    const products = data?.resources?.results?.products ?? [];

    const parts = [];

    for (const suggestion of suggestions.slice(0, 5)) {
      const [matched, rest] = splitSuggestion(suggestion.text, query);
      const href = `/search?q=${encodeURIComponent(suggestion.text)}`;
      parts.push(
        `<li class="rubato-search-drawer__row"><a class="rubato-search-drawer__suggestion" href="${escapeHtml(href)}">` +
          `${escapeHtml(matched)}<span class="rubato-search-drawer__suggestion-rest">${escapeHtml(rest)}</span>` +
          `</a></li>`
      );
    }

    let html = parts.length ? `<ul class="rubato-search-drawer__rows">${parts.join('')}</ul>` : '';

    /* The cards are Liquid's, not this file's: the list arrives rendered by
       sections/rubato-search-cards.liquid, carrying the collection's grid and card classes, and
       goes in whole. On a wide screen they are put in the page cell instead of the panel. */
    const wide = isWide();
    if (cards.length && !wide) html += cards[0].outerHTML;

    const cardCount = cards.length ? cards[0].querySelectorAll('.rubato-search-drawer__card').length : 0;

    /* Round 62 part 5. The count is the catalogue's, not the drawer's.
     *
     * It read the length of the products array, which the request caps at four, so a search
     * with seventy matches offered "View all (2)" beside the cards. There is no total in the
     * predictive response to use instead: /search/suggest.json returns products and queries
     * and nothing else. The number comes from the cards section, which is asked for with
     * type=product and hands back search.results_count on the list.
     *
     * The link carries type=product with it, so the page the customer lands on holds the
     * number they were shown rather than that number plus articles and pages. */
    const total = cards.length ? Number(cards[0].dataset.total || 0) || cardCount : cardCount;
    const viewAll = `<a class="rubato-search-drawer__all" href="/search?q=${encodeURIComponent(query)}&type=product">View all (${total})</a>`;

    if (!parts.length && !cardCount) {
      html = `<p class="rubato-search-drawer__none">Nothing matches ${escapeHtml(query)}.</p>`;
    } else if (!wide) {
      html += viewAll;
    }

    results.innerHTML = html;
    results.hidden = false;
    if (empty) empty.hidden = true;

    /* The page cell: the grid and the count under it, or nothing and transparent. */
    if (page) {
      /* At a wide width the cell always holds its column, filled or not: hiding it let the
         panel fall into the left column and the drawer sat on the wrong side of the screen
         until the first result arrived. Empty it is transparent and the backdrop dims through
         it, which is the same thing the eye saw before. */
      if (wide) {
        page.hidden = false;
        page.innerHTML = cardCount ? cards[0].outerHTML + viewAll : '';
      } else {
        page.hidden = true;
        page.replaceChildren();
      }
    }
  };

  /* The section comes back as a full HTML document fragment; only the product cards are
     wanted out of it. Parsed rather than injected whole, so nothing of the results page's own
     chrome, filters or pagination reaches the drawer. */
  const fetchCards = async (query) => {
    try {
      const response = await fetch(`${SECTION}&q=${encodeURIComponent(query)}`);
      if (!response.ok) return [];
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      /* The whole list, not the items out of it. The ul carries product-grid, which is where
         the collection's column rules live; lifting the li elements into a new wrapper threw
         that away and the cards came back in one column. */
      const list = parsed.querySelector('.rubato-search-drawer__cards');
      return list ? [list] : [];
    } catch (error) {
      return [];
    }
  };

  const run = async (query) => {
    const mine = ++sequence;
    try {
      const url =
        `${ENDPOINT}?q=${encodeURIComponent(query)}` +
        `&resources[type]=product,query&resources[limit]=${LIMIT}&resources[options][unavailable_products]=last`;
      /* Both at once: the suggestions and the count come from the JSON, the cards from the
         section, and waiting for them in series would double the time to first paint. */
      const [response, cards] = await Promise.all([
        fetch(url, { headers: { Accept: 'application/json' } }),
        fetchCards(query),
      ]);
      if (!response.ok) return;
      const data = await response.json();
      /* An answer that arrives after a later one is thrown away, or a slow request for "de"
         overwrites the results for "denim". */
      if (mine !== sequence) return;
      render(data, query, cards);
    } catch (error) {
      /* A search that cannot reach the endpoint leaves what is on screen alone. */
    }
  };

  input?.addEventListener('input', () => {
    const query = input.value.trim();
    clearTimeout(timer);
    if (!query) {
      sequence++;
      showEmptyState();
      return;
    }
    timer = setTimeout(() => run(query), DEBOUNCE);
  });

  input?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const query = input.value.trim();
    if (!query) return;
    event.preventDefault();
    window.location.href = `/search?q=${encodeURIComponent(query)}`;
  });
}

setUpSearch();
document.addEventListener('shopify:section:load', setUpSearch);
